import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireRemoteConfig } from '@angular/fire/compat/remote-config';
import { AllPermissionsResponse } from 'src/app/models/permissions.model';
import { environment } from 'src/environments/environment';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
   providedIn: 'root',
})
export class FirebaseRemoteConfigService {
   private isBrowser: boolean;

   constructor(
      private remote_config: AngularFireRemoteConfig,
      @Inject(PLATFORM_ID) private platformId: Object
   ) {
      this.isBrowser = isPlatformBrowser(this.platformId);
      if (this.isBrowser) {
         this.remote_config.fetchAndActivate();
      }
   }

   private cachedFreeCollections: { stocks: string[], etfs: string[] } | null = null;

   public async getFreeStocksList() {
      if (!this.isBrowser) return [];

      if (!this.cachedFreeCollections) {
         this.cachedFreeCollections = JSON.parse(
            await this.remote_config.getString('free_collections')
         );
      }
      return this.cachedFreeCollections?.stocks ?? [];
   }

   public async getFreeEtfList() {
      if (!this.isBrowser) return [];

      if (!this.cachedFreeCollections) {
         this.cachedFreeCollections = JSON.parse(
            await this.remote_config.getString('free_collections')
         );
      }
      return this.cachedFreeCollections?.etfs ?? [];
   }

   public async getBucketList() {
      if (!this.isBrowser) return [];
      return JSON.parse(
         await this.remote_config.getString('free_buckets_collection')
      );
   }

   public async monthlyFreeCompliance() {
      if (!this.isBrowser) return null;
      return JSON.parse(await this.remote_config.getString('config_defaults'))
         ?.monthly_free_stock_count;
   }

   public async activePromotionalOffer() {
      if (!this.isBrowser) return null;
      return JSON.parse(await this.remote_config.getString('config_defaults'))
         ?.active_promotional_offer;
   }

   public async getcbaStatusDisclaimer() {
      if (!this.isBrowser) return null;
      return JSON.parse(await this.remote_config.getString('config_defaults'))
         ?.cba_status_disclaimer;
   }

   public async countryPricingPlans() {
      if (!this.isBrowser) return null;
      let countryPricingPlans = null;
      let userCountry: any = localStorage.getItem('userCountry');
      userCountry = JSON.parse(userCountry);
      var countryList = await JSON.parse(
         await this.remote_config.getString('country_pricing_plans')
      );
      await countryList.forEach((countryPlans: any) => {
         if (userCountry != null && userCountry.length) {
            if (
               countryPlans.country_code === userCountry[0].code.toLowerCase()
            ) {
               countryPricingPlans = countryPlans;
               return;
            }
         }
      });

      if (countryPricingPlans == null) {
         countryPricingPlans = countryList.find(
            (item: any) => item.country_code == 'default'
         );
      }
      return countryPricingPlans;
   }

   public async promoOffersConfig() {
      if (!this.isBrowser) return {};
      let promoOffer = {};
      let activePromotionalOfferName = await this.activePromotionalOffer();
      await JSON.parse(
         await this.remote_config.getString('promo_offers_config')
      )?.forEach((offerConfig: any) => {
         if (offerConfig.name == activePromotionalOfferName) {
            promoOffer = offerConfig;
            return;
         }
      });
      return promoOffer;
   }

   public async getConfigDefault() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('config_defaults')
      );
   }

   public async getMusaffaAcademy() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('musaffa_academy')
      );
   }

   public async getInAppSubscriptionDefaults() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('inapp_subscription_defaults')
      );
   }

   public async userOnboardingdefaults() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('user_onboarding_defaults')
      );
   }

   public async getPaywallExperiments() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('paywall_experiments')
      );
   }

   public async getMarketingBanners() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('marketing_banners')
      );
   }

   public async getPurificationDefaults() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('purification_defaults')
      );
   }

   public async getPortfolioDefaults() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('portfolio_defaults')
      );
   }

   public async getPurificationArticles() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('purification_articles')
      );
   }

   public async getPortfolioArticles() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('portfolio_articles')
      );
   }

   public async getComplianceData() {
      if (!this.isBrowser) return [];
      var complianceJson: any = [
         { key: '', display_value: 'All', isAll: true },
         { key: 'COMPLIANT', display_value: 'Halal' },
         { key: 'NON_COMPLIANT', display_value: 'Not Halal' },
         { key: 'QUESTIONABLE', display_value: 'Doubtful' },
      ];
      return complianceJson;
   }

   public getComplianceScoreData() {
      if (!this.isBrowser) return [];
      var complianceScoreJson: any = [
         {
            key: '1',
            display_value: '1',
         },
         {
            key: '2',
            display_value: '2',
         },
         {
            key: '3',
            display_value: '3',
         },
         {
            key: '4',
            display_value: '4',
         },
         {
            key: '5',
            display_value: '5',
         },
      ];
      return complianceScoreJson;
   }

   public getAnalystRatings() {
      if (!this.isBrowser) return [];
      var analystRatingsJson: any = [
         {
            key: 'Strong buy',
            display_value: 'Strong Buy',
         },
         {
            key: 'Buy',
            display_value: 'Buy',
         },
         {
            key: 'Hold',
            display_value: 'Hold',
         },
         {
            key: 'Sell',
            display_value: 'Sell',
         },
         {
            key: 'Strong sell',
            display_value: 'Strong Sell',
         },
      ];
      return analystRatingsJson;
   }

   public getSortBy() {
      if (!this.isBrowser) return [];
      var sortByJson: any = [
         {
            key: '',
            display_value: 'None',
         },
         {
            key: 'currentPrice:asc',
            display_value: 'Price: Low to High',
         },
         {
            key: 'currentPrice:desc',
            display_value: 'Price: High to Low',
         },
         {
            key: 'percentChange:asc',
            display_value: 'Percent Change: Low to High',
         },
         {
            key: 'percentChange:desc',
            display_value: 'Percent Change: High to Low',
         },
         {
            key: 'compliantRanking:asc',
            display_value: 'Compliance: Low to High',
         },
         {
            key: 'compliantRanking:desc',
            display_value: 'Compliance: High to Low',
         },
      ];
      return sortByJson;
   }

   public getEtfAssetClassData() {
      if (!this.isBrowser) return [];
      var etfAssetClassJson: any = [
         { key: 'Equity', display_value: 'Equity' },
         { key: 'Fixed Income', display_value: 'Fixed Income' },
      ];
      return etfAssetClassJson;
   }

   public async getFeatureVisibilityDefaults() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('feature_visibility_flags')
      );
   }

   public async getPaywallDefaults() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('paywall_defaults')
      );
   }

   public async getPromoOffers() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('promo_offers_config')
      );
   }

   public async getRequestReportUpdationDefaults() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('request_report_updation_defaults')
      );
   }

   public async getCrowdfundingDefaults() {
      if (!this.isBrowser) return null;
      return await JSON.parse(
         await this.remote_config.getString('crowdfunding_defaults')
      );
   }

   public async getSubscriptionPlanConfig(): Promise<AllPermissionsResponse> {
      if (!this.isBrowser) return { plans: [], features: [] };
      const config = await JSON.parse(
         await this.remote_config.getString('subscription_plans_config')
      );
      const permissionsResponse: AllPermissionsResponse = {
         plans: config.plans,
         features: config.features
      }
      return permissionsResponse;
   }

   public async getVerificationDefaults() {
      return await JSON.parse(
         await this.remote_config.getString('verification_defaults')
      );
   }
}
